/**
 * Stable token metadata for one highlighted word in a testimonial quote.
 */
export interface HighlightWordToken {
    id: string;
    text: string;
    leadingWhitespace: string;
}

/**
 * Tokenized representation of one highlight segment, including preserved trailing whitespace.
 */
export interface TokenizedHighlightSegment {
    words: HighlightWordToken[];
    trailingWhitespace: string;
}

/**
 * Tokenized representation of all highlight segments, flattened for the motion layer and grouped for rendering.
 */
export interface TokenizedHighlights {
    segments: TokenizedHighlightSegment[];
    words: HighlightWordToken[];
}

/**
 * Splits one highlight segment into stable per-word tokens so matching words can be reused across layers.
 */
function tokenizeHighlightSegment(highlight: string, startingWordIndex: number): TokenizedHighlightSegment {
    const words: HighlightWordToken[] = [];
    const tokenPattern = /(\s*)(\S+)/g;
    let match: RegExpExecArray | null = null;
    let lastIndex = 0;

    while ((match = tokenPattern.exec(highlight)) !== null) {
        words.push({
            id: `highlight-word-${startingWordIndex + words.length}`,
            leadingWhitespace: match[1],
            text: match[2]
        });
        lastIndex = tokenPattern.lastIndex;
    }

    if (words.length === 0) {
        return {
            words: [
                {
                    id: `highlight-word-${startingWordIndex}`,
                    leadingWhitespace: "",
                    text: highlight
                }
            ],
            trailingWhitespace: ""
        };
    }

    return {
        words,
        trailingWhitespace: highlight.slice(lastIndex)
    };
}

/**
 * Splits every highlight segment into stable per-word tokens so the collapsed and expanded layers share ids.
 */
export function tokenizeHighlights(highlights: string[]): TokenizedHighlights {
    const segments: TokenizedHighlightSegment[] = [];
    const words: HighlightWordToken[] = [];

    highlights.forEach((highlight) => {
        const segment = tokenizeHighlightSegment(highlight, words.length);
        segments.push(segment);
        words.push(...segment.words);
    });

    return {
        segments,
        words
    };
}

/**
 * Parses a CSS duration string like `240ms` or `0.24s` into milliseconds and falls back on invalid values.
 */
export function parseCssDuration(rawValue: string, fallbackMs: number): number {
    const value = rawValue.trim();
    if (!value) {
        return fallbackMs;
    }

    if (value.endsWith("ms")) {
        const parsed = Number.parseFloat(value.slice(0, -2));
        return Number.isFinite(parsed) ? parsed : fallbackMs;
    }

    if (value.endsWith("s")) {
        const parsed = Number.parseFloat(value.slice(0, -1));
        return Number.isFinite(parsed) ? parsed * 1000 : fallbackMs;
    }

    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallbackMs;
}

/**
 * Restricts testimonial avatar URLs to local relative paths.
 */
export function isAllowedPersonImage(image: string | null): image is string {
    return typeof image === "string" &&
        (image.startsWith("./") || (image.startsWith("/") && !image.startsWith("//")));
}
